import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useReactive } from 'ahooks';
import { PANEL_MODE, PANEL_TAB } from '../components/constants';

const EditorContext = createContext(null);

/**
 * EditorContext Provider
 * 编辑器页面各部分（顶栏 / 大纲 / 正文 / 工具面板）共享的状态
 */
export const EditorContextProvider = ({ children }) => {
    const [editor, setEditor] = useState(null);

    // 正文滚动容器，供大纲定位、章节滚动使用
    const scrollContainerRef = useRef(null);

    // UI 状态
    const uiState = useReactive({
        loading: true,
        loadError: '',
        paperTitle: '',
        outlineCollapsed: false, // 桌面端：大纲是否收起
        outlineOpen: false, // 窄屏：大纲抽屉
        toolsOpen: false, // 窄屏：工具面板抽屉
        activeTab: PANEL_TAB.CHAPTER, // 工具面板当前页签
        panelMode: PANEL_MODE.EMPTY, // 工具面板识别到的选中对象（镜像，供顶栏 / 移动端提示使用）
    });

    // 选中状态
    const selectionState = useReactive({
        selectedText: '',
        selectedBlockIds: [],
        selectedRange: { from: null, to: null },
        isHeadingSelected: false,
        selectedHeadingInfo: null, // { node, pos, level, detailId, blockId }
        version: 0, // 每次选区变化 +1，用于触发依赖方刷新
    });

    // 文档状态
    const documentState = useReactive({
        orderNo: null,
        editorContent: null,
    });

    // 保存状态
    const saveState = useReactive({
        status: 'idle', // 'idle' | 'saving' | 'saved'
        lastSavedTime: null,
    });

    const [generating, setGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationStatus, setGenerationStatus] = useState('');

    // 章节改稿遮罩状态
    const [chapterRewriting, setChapterRewriting] = useState(false);
    const [chapterRewriteInfo, setChapterRewriteInfo] = useState(null); // { blockId, pos, detailId, level }
    const [chapterRewriteProgress, setChapterRewriteProgress] = useState(0);
    const [chapterRewriteStatus, setChapterRewriteStatus] = useState('');
    // 由工具面板注册：中断改稿任务的方法
    const rewriteCancelRef = useRef(null);

    const updateGenerationState = useCallback((state) => {
        if (state.generating !== undefined) setGenerating(state.generating);
        if (state.progress !== undefined) setGenerationProgress(state.progress);
        if (state.status !== undefined) setGenerationStatus(state.status);
    }, []);

    const resetGenerationState = useCallback(() => {
        setGenerating(false);
        setGenerationProgress(0);
        setGenerationStatus('');
    }, []);

    const startChapterRewrite = useCallback((chapterInfo) => {
        setChapterRewriting(true);
        setChapterRewriteInfo(chapterInfo);
        setChapterRewriteProgress(0);
        setChapterRewriteStatus('正在提交改稿任务…');
    }, []);

    const updateChapterRewriteState = useCallback((state) => {
        if (state.progress !== undefined) setChapterRewriteProgress(state.progress);
        if (state.status !== undefined) setChapterRewriteStatus(state.status);
    }, []);

    const finishChapterRewrite = useCallback(() => {
        setChapterRewriting(false);
        setChapterRewriteInfo(null);
        setChapterRewriteProgress(0);
        setChapterRewriteStatus('');
    }, []);

    const registerRewriteCanceller = useCallback((fn) => {
        rewriteCancelRef.current = fn;
    }, []);

    // 正文重新加载（由页面注册 loadDocument），供改稿应用失败时兜底
    const documentReloaderRef = useRef(null);
    const registerDocumentReloader = useCallback((fn) => {
        documentReloaderRef.current = fn;
    }, []);
    const reloadDocument = useCallback(async () => {
        if (typeof documentReloaderRef.current === 'function') {
            await documentReloaderRef.current();
        }
    }, []);

    const interruptChapterRewrite = useCallback(() => {
        if (typeof rewriteCancelRef.current === 'function') {
            rewriteCancelRef.current();
        }
        finishChapterRewrite();
    }, [finishChapterRewrite]);

    /** 打开工具面板的某个页签（窄屏时同时打开抽屉） */
    const openToolPanel = useCallback(
        (tab) => {
            if (tab) uiState.activeTab = tab;
            uiState.toolsOpen = true;
        },
        [uiState],
    );

    const value = {
        editor,
        setEditor,
        scrollContainerRef,
        uiState,
        selectionState,
        documentState,
        saveState,
        generating,
        generationProgress,
        generationStatus,
        updateGenerationState,
        resetGenerationState,
        chapterRewriting,
        chapterRewriteInfo,
        chapterRewriteProgress,
        chapterRewriteStatus,
        startChapterRewrite,
        updateChapterRewriteState,
        finishChapterRewrite,
        interruptChapterRewrite,
        registerRewriteCanceller,
        registerDocumentReloader,
        reloadDocument,
        openToolPanel,
    };

    return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};

export const useEditorContext = () => {
    const context = useContext(EditorContext);
    if (!context) {
        throw new Error('useEditorContext must be used within EditorContextProvider');
    }
    return context;
};
